// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CostCategoryAutocomplete } from '@/costs/components/cost-category-autocomplete';
import { CategoryType } from '@/costs/data/cost-categories';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  PathValue,
} from 'react-hook-form';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  /**
   * The name of the form input element.
   */
  name: TName;

  /**
   * The control from react-hook-form to register this field.
   */
  control: Control<TFieldValues>;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * The placeholder text displayed when no value is selected.
   */
  placeholder?: string;

  /**
   * The size of the input field.
   */
  size?: 'small' | 'medium';

  /**
   * The type of category to show ('time' or 'cost'). Defaults to 'cost'.
   */
  type?: CategoryType;
}

/**
 * A react-hook-form wrapper for the CostCategoryAutocomplete component.
 */
export function FormCostCategoryAutocomplete<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>) {
  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field, fieldState }) => (
        <CostCategoryAutocomplete
          {...field}
          size={props.size}
          label={props.label}
          placeholder={props.placeholder}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          value={field.value as PathValue<TFieldValues, TName>}
          onChange={(value) => {
            field.onChange(value as PathValue<TFieldValues, TName>);
          }}
          type={props.type}
        />
      )}
    />
  );
}
